import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Eye, EyeOff, Gift, MailCheck, Phone, RefreshCw } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";

function generatedUsername(fullName, suffix) {
  const parts = String(fullName || "").toLowerCase().match(/[a-z0-9]+/g) || [];
  const base = parts.length >= 2 ? `${parts[0]}${parts[parts.length - 1]}` : (parts[0] || "player");
  return `${base.slice(0, 22)}${suffix}`;
}

export default function Signup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [search] = useSearchParams();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", referral_code: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [usernameSuffix] = useState(() => String(Math.floor(1000 + Math.random() * 9000)));
  const displayUsername = useMemo(() => generatedUsername(form.full_name, usernameSuffix), [form.full_name, usernameSuffix]);

  // Pre-fill referral code from URL (?ref=ABC123)
  useEffect(() => {
    const ref = search.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
      setShowReferral(true);
    }
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const fullName = form.full_name.trim().replace(/\s+/g, " ");
      if (fullName.split(" ").length < 2) {
        setErr("Enter your full first and last name.");
        setLoading(false);
        return;
      }
      const payload = { ...form, full_name: fullName, username: generatedUsername(fullName, usernameSuffix) };
      if (!payload.referral_code) delete payload.referral_code;
      if (!payload.phone) delete payload.phone;
      const { data } = await api.post("/auth/register", payload);
      if (data?.email_verification_required && !data?.token) {
        setVerification({
          email: data.email || payload.email,
          message: data.message || "Verification email sent. Check your Gmail to finish signup.",
          codeRequired: Boolean(data.code_required),
        });
        return;
      }
      login(data.token, data.user);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    const cleanCode = verificationCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setErr("Enter the 6-digit Gmail code.");
      return;
    }
    setErr("");
    setResendMessage("");
    setVerifyLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify", {
        email: verification?.email || form.email,
        code: cleanCode,
      });
      login(data.token, data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setVerifyLoading(false);
    }
  };

  const resendVerification = async () => {
    const targetEmail = verification?.email || form.email;
    if (!targetEmail) return;
    setErr("");
    setResendMessage("");
    setResendLoading(true);
    try {
      const { data } = await api.post("/auth/email/resend", { email: targetEmail });
      setResendMessage(data?.message || "Verification email sent. Check your Gmail.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResendLoading(false);
    }
  };

  if (verification) {
    return (
      <div className="min-h-screen bg-[#030705] text-white">
        <Navbar />
        <div className="max-w-md mx-auto px-6 pt-16 pb-24">
          <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#07110B] p-6 shadow-2xl shadow-[#D4AF37]/10">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-5">
              <MailCheck className="w-7 h-7 text-[#F1C40F]" />
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#F1C40F] mb-2">Verify email</p>
            <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Enter Gmail code</h1>
            <p className="text-white/70 leading-relaxed">
              {verification.message} Your account will be created only after this code is verified.
            </p>
            <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/70 break-all">
              {verification.email}
            </div>
            <form onSubmit={verifyCode} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Gmail code</label>
                <input
                  data-testid="signup-email-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-[#D4AF37]/40 focus:border-[#D4AF37] focus:outline-none text-center text-2xl font-black tracking-[0.5em]"
                />
              </div>
              <button
                data-testid="signup-verify-code"
                type="submit"
                disabled={verifyLoading}
                className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#F1C40F] transition-all disabled:opacity-50"
              >
                {verifyLoading ? "Creating account..." : "Verify code & create account"}
              </button>
            </form>
            {resendMessage && (
              <div className="mt-4 text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3">
                {resendMessage}
              </div>
            )}
            {err && (
              <div data-testid="signup-error" className="mt-4 text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">
                {err}
              </div>
            )}
            <button
              type="button"
              onClick={resendVerification}
              disabled={resendLoading}
              className="mt-4 w-full py-3 border border-[#D4AF37]/40 text-[#F1C40F] font-bold rounded-full hover:bg-[#D4AF37]/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${resendLoading ? "animate-spin" : ""}`} />
              {resendLoading ? "Sending..." : "Resend Gmail code"}
            </button>
            <p className="mt-5 text-sm text-white/60 text-center">
              Already verified? <Link to="/login" className="text-[#D4AF37] hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Create account</h1>
        <p className="text-white/60 mb-4">Free to play · No credit card · 18+ only</p>
        <p className="mb-8 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-3 text-sm text-white/70">One account per person and full legal name. Each device can register up to 2 accounts for different people. Use the name shown on your ID.</p>
        <form onSubmit={submit} autoComplete="on" className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Full name</label>
            <input data-testid="signup-full-name" name="name" autoComplete="name" required minLength={2} value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Display username</label>
            <input data-testid="signup-generated-username" name="nickname" autoComplete="nickname" readOnly value={displayUsername}
              className="w-full px-4 py-3 rounded-lg bg-[#07110B] border border-white/10 text-[#F1C40F] outline-none" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Email</label>
            <input data-testid="signup-email" type="email" name="email" autoComplete="username" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2 flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> Phone number <span className="text-white/30 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              data-testid="signup-phone"
              type="tel"
              name="tel"
              autoComplete="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e)=>setForm({...form, phone: e.target.value.replace(/[^\d()+\-\s]/g, "").slice(0, 20)})}
              placeholder="Phone number"
              className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Password</label>
            <div className="relative">
              <input data-testid="signup-password" type={showPassword ? "text" : "password"} name="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})}
                className="w-full px-4 py-3 pr-12 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none" />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/50 hover:bg-white/10 hover:text-[#F1C40F]"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Referral code — collapsed by default */}
          {!showReferral ? (
            <button
              type="button"
              onClick={() => setShowReferral(true)}
              data-testid="signup-show-referral"
              className="flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F1C40F]"
            >
              <Gift className="w-4 h-4" /> Have a referral code? Add it
            </button>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2 flex items-center gap-2">
                <Gift className="w-3.5 h-3.5 text-[#D4AF37]" /> Referral code <span className="text-white/30 normal-case tracking-normal">(optional)</span>
              </label>
              <input
                data-testid="signup-referral-code"
                value={form.referral_code}
                onChange={(e)=>setForm({...form, referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)})}
                placeholder="e.g. SOPHIA7"
                maxLength={12}
                className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-[#D4AF37]/40 focus:border-[#D4AF37] focus:outline-none uppercase tracking-[0.3em] font-mono"
              />
            </div>
          )}

          {err && <div data-testid="signup-error" className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">{err}</div>}
          <button data-testid="signup-submit" type="submit" disabled={loading}
            className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#F1C40F] transition-all disabled:opacity-50">
            {loading ? "Sending code..." : "Create account"}
          </button>
        </form>
        <p className="mt-8 text-sm text-white/60 text-center">
          Have an account? <Link to="/login" data-testid="signup-login-link" className="text-[#D4AF37] hover:underline">Sign in</Link>
        </p>
        <p className="mt-4 text-xs text-white/40 text-center">By signing up you agree to {siteConfig.name} terms. 18+ only.</p>
      </div>
    </div>
  );
}
